import React, { useState } from 'react';
import { useChat } from '../../contexts/ChatContext';

interface ChatIconProps {
  className?: string;
}

const ChatIcon: React.FC<ChatIconProps> = ({ className = '' }) => {
  const { toggleChat, isOpen, approvalRequests } = useChat();
  const [isHovered, setIsHovered] = useState(false);

  const pendingApprovals = approvalRequests.filter(r => r.status === 'pending').length;

  const buttonStyle: React.CSSProperties = {
    position: 'relative',
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    background: isOpen
      ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(6, 182, 212, 0.2))'
      : isHovered
        ? 'rgba(139, 92, 246, 0.15)'
        : 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: isOpen
      ? '0 0 20px rgba(139, 92, 246, 0.4), inset 0 0 20px rgba(139, 92, 246, 0.1)'
      : '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
    transform: isHovered && !isOpen ? 'scale(1.05)' : 'scale(1)',
  };

  return (
    <button
      className={`chat-icon-btn ${isOpen ? 'active' : ''} ${className}`}
      style={buttonStyle}
      onClick={toggleChat}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label="Toggle AI Assistant"
      title="AI Assistant"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        style={{
          filter: isOpen ? 'drop-shadow(0 0 6px rgba(139, 92, 246, 0.6))' : 'none',
          transition: 'filter 0.3s ease',
        }}
      >
        {/* Gradient Definition */}
        <defs>
          <linearGradient id="chatIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(139, 92, 246, 0.4)" />
            <stop offset="100%" stopColor="rgba(6, 182, 212, 0.3)" />
          </linearGradient>
        </defs>

        {/* Main Chat Bubble */}
        <path
          d="M20 14c0 1.1-.9 2-2 2H8l-4 4V6c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v8z"
          fill={isOpen ? 'url(#chatIconGradient)' : 'none'}
          stroke={isOpen ? '#8b5cf6' : 'var(--text-secondary)'}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* AI Sparkle */}
        <path
          d="M18 2l.5 1.5L20 4l-1.5.5L18 6l-.5-1.5L16 4l1.5-.5L18 2z"
          fill={isOpen ? '#8b5cf6' : 'var(--text-tertiary)'}
        >
          <animate
            attributeName="opacity"
            values="1;0.5;1"
            dur="2s"
            repeatCount="indefinite"
          />
        </path>

        {/* Typing Dots */}
        <circle cx="9" cy="10" r="1.2" fill={isOpen ? '#8b5cf6' : 'var(--text-tertiary)'}>
          <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" begin="0s" />
        </circle>
        <circle cx="12" cy="10" r="1.2" fill={isOpen ? '#06b6d4' : 'var(--text-tertiary)'}>
          <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" begin="0.2s" />
        </circle>
        <circle cx="15" cy="10" r="1.2" fill={isOpen ? '#8b5cf6' : 'var(--text-tertiary)'}>
          <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" begin="0.4s" />
        </circle>
      </svg>

      {/* Notification Badge */}
      {pendingApprovals > 0 && (
        <span
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-6px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid var(--bg-primary)',
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.5)',
            animation: 'badge-pulse 2s infinite',
          }}
        >
          {pendingApprovals > 9 ? '!' : pendingApprovals}
        </span>
      )}
    </button>
  );
};

export default ChatIcon;
